import { useEffect, useMemo, useRef, useState } from 'react';

import { useMutation, useQuery } from '@apollo/client/react';
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { ExternalLink, Pencil } from 'lucide-react';
import type mapboxgl from 'mapbox-gl';
import { Link } from 'react-router-dom';

import { gql } from '@graphql/__generated__';
import type { Leads_Bool_Exp } from '@graphql/__generated__/graphql';
import {
  Leads_Status_Enum,
  Place_Type_Enum_Enum,
} from '@graphql/__generated__/graphql';
import { Mapbox } from '@modules/_shared/components/Mapbox';
import { PlacePicker } from '@modules/_shared/components/place-picker/PlacePicker';
import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import { useLocale } from '@shared/hooks/locale';

const compactContainedButtonSx = {
  minHeight: 28,
  px: 1.25,
  py: 0.25,
  fontSize: '0.75rem',
  lineHeight: 1.2,
  boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
} as const;

export const GET_CATCHMENT_AREA = gql(/* GraphQL */ `
  query GetCatchmentArea($userId: uuid!) {
    users_by_pk(id: $userId) {
      catchment_areas {
        place {
          id
          postcode
          population
          lat
          lng
        }
      }
    }
  }
`);

const SET_CATCHMENT_AREA = gql(/* GraphQL */ `
  mutation SetCatchmentArea(
    $userId: uuid!
    $objects: [catchment_areas_insert_input!]!
  ) {
    delete_catchment_areas(where: { user_id: { _eq: $userId } }) {
      affected_rows
    }
    insert_catchment_areas(objects: $objects) {
      affected_rows
    }
  }
`);

type UserCatchmentAreaProps = {
  userId: string;
  embedded?: boolean;
};

export const UserCatchmentArea = ({
  userId,
  embedded = false,
}: UserCatchmentAreaProps) => {
  const { me } = useAppData();
  const isAdmin = me?.is_admin;
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const mapbox = useRef<mapboxgl.Map | null>(null);
  const [mapReady, setMapReady] = useState(false);
  const { t } = useLocale();
  const { data, refetch } = useQuery(GET_CATCHMENT_AREA, {
    variables: { userId },
  });

  const [setCatchmentArea] = useMutation(SET_CATCHMENT_AREA);

  const boundingBox: [number, number, number, number] | undefined = useMemo(
    () =>
      (data?.users_by_pk?.catchment_areas.length ?? 0) >= 1
        ? data?.users_by_pk?.catchment_areas.reduce<
            [number, number, number, number]
          >(
            ([minLng, minLat, maxLng, maxLat], { place: { lat, lng } }) => [
              Math.min(minLng, lng),
              Math.min(minLat, lat),
              Math.max(maxLng, lng),
              Math.max(maxLat, lat),
            ],
            [Infinity, Infinity, -Infinity, -Infinity],
          )
        : undefined,
    [data],
  );

  const localitiesIds = useMemo(
    () => data?.users_by_pk?.catchment_areas.map(({ place }) => place.id) ?? [],
    [data],
  );

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const whereClause = useMemo(() => {
    const where: Leads_Bool_Exp = {
      completed: { _eq: true },
      _not: {
        lead_agents: {
          user: {},
          lead_stage: {
            status: { _in: [Leads_Status_Enum.Lost, Leads_Status_Enum.Won] },
          },
        },
      },
      property: {
        places: {
          id: {
            _in: localitiesIds,
          },
        },
      },
    };

    return JSON.stringify(where);
  }, [localitiesIds]);

  const leadsLink = {
    pathname: '/leads',
    search: `where=${encodeURIComponent(whereClause)}`,
  };

  useEffect(() => {
    if (!mapbox.current) {
      return;
    }

    mapbox.current?.on('load', () => {
      mapbox.current?.addSource('localities', {
        type: 'vector',
        url: `mapbox://spingwun.amdtvcir`,
      });

      mapbox.current?.addLayer({
        id: 'localities',
        type: 'fill',
        source: 'localities',
        'source-layer': 'crm-locality-poly-0j1cq1',
        paint: {
          'fill-color': '#2196F3',
          'fill-opacity': 0.35,
        },
        filter: ['in', 'id', ...[]],
      });

      mapbox.current?.addLayer({
        id: 'localities-outline',
        type: 'line',
        source: 'localities',
        'source-layer': 'crm-locality-poly-0j1cq1',
        paint: {
          'line-color': '#2196F3',
          'line-width': 2,
        },
        filter: ['in', 'id', ...[]],
      });

      setMapReady(true);
    });
  }, []);

  useEffect(() => {
    if (mapbox.current && boundingBox) {
      const map = mapbox.current;

      // Perform the fitBounds operation
      map.fitBounds(boundingBox, {
        padding: 100,
        duration: 250,
        maxZoom: 12,
      });

      // One-time handler for when initial fitBounds completes
      const onInitialMoveEnd = () => {
        if (map.getZoom() < 7.5) {
          map.setZoom(7.5);
        }
        // Remove the listener after first execution
        map.off('moveend', onInitialMoveEnd);
      };

      // Add the one-time event listener
      map.on('moveend', onInitialMoveEnd);
    }
  }, [boundingBox]);

  useEffect(() => {
    if (mapbox.current?.getLayer('localities')) {
      mapbox.current?.setFilter('localities', ['in', 'id', ...localitiesIds]);
    }
    if (mapbox.current?.getLayer('localities-outline')) {
      mapbox.current?.setFilter('localities-outline', [
        'in',
        'id',
        ...localitiesIds,
      ]);
    }
  }, [localitiesIds, mapReady]);

  const mapBlock = (
    <Box
      sx={{
        mt: embedded ? 0 : 1,
        minHeight: 240,
        height: '100%',
        position: 'relative',
        width: '100%',
        border: '1px solid rgba(0, 0, 0, 0.2)',
        borderRadius: 2,
        overflow: 'hidden',
        ...theme.applyStyles('dark', {
          border: '1px solid rgba(255, 255, 255, 0.2)',
        }),
      }}
    >
      <Mapbox defaultZoom={10} mapRef={mapbox} />
      <Box
        sx={{
          p: 1,
          position: 'absolute',
          top: 10,
          left: 10,
          background: 'rgba(255, 255, 255, 0.25)',
          borderRadius: 2,
          backdropFilter: 'blur(15px)',
          boxShadow: '0 8px 32px rgba(31, 38, 135, 0.15)',
          border: '1px solid rgba(255, 255, 255, 0.18)',
          ...theme.applyStyles('dark', {
            background: 'rgba(8, 10, 14, 0.35)',
            border: '1px solid rgba(255, 255, 255, 0.12)',
          }),
        }}
      >
        <table>
          <tbody>
            <tr>
              <td>
                <strong>{t('Population')}</strong>
              </td>
              <td align="right" style={{ paddingLeft: 25 }}>
                {data?.users_by_pk?.catchment_areas
                  .reduce((acc, { place }) => acc + (place.population ?? 0), 0)
                  .toLocaleString()}
              </td>
            </tr>
            <tr>
              <td>
                <strong>{t('Max distance')}</strong>
              </td>
              <td align="right" style={{ paddingLeft: 25 }}>
                {boundingBox
                  ? `${Math.round(
                      Math.sqrt(
                        Math.pow(
                          (boundingBox[3] - boundingBox[1]) * 111.32,
                          2,
                        ) +
                          Math.pow(
                            (boundingBox[2] - boundingBox[0]) *
                              111.32 *
                              Math.cos(Math.PI / 4),
                            2,
                          ),
                      ),
                    ).toLocaleString()} km`
                  : ''}
              </td>
            </tr>
          </tbody>
        </table>
        {(data?.users_by_pk?.catchment_areas?.length ?? 0) > 0 && (
          <Button
            variant="contained"
            color="primary"
            size="small"
            component={Link}
            to={leadsLink}
            target="_blank"
            startIcon={<ExternalLink size={12} />}
            sx={{ ...compactContainedButtonSx, mt: 1, width: '100%' }}
          >
            {t('View leads')}
          </Button>
        )}
      </Box>
      {isAdmin && (
        <Button
          variant="contained"
          color="primary"
          size="small"
          onClick={() => setOpen(true)}
          startIcon={<Pencil size={12} />}
          sx={{
            ...compactContainedButtonSx,
            position: 'absolute',
            top: 10,
            right: 10,
          }}
        >
          {t('Edit')}
        </Button>
      )}
    </Box>
  );

  return (
    <>
      {embedded ? (
        mapBlock
      ) : (
        <Box sx={{ p: 2, mb: 2, position: 'relative' }}>
          <Typography variant="h6">{t('Catchment area')}</Typography>
          {mapBlock}
        </Box>
      )}
      {error && (
        <Box sx={{ m: 2 }}>
          <Alert severity="error">
            <pre>{JSON.stringify(error, null, 2)}</pre>
          </Alert>
        </Box>
      )}
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        fullWidth
        fullScreen={isMobile}
        maxWidth="lg"
        slotProps={{
          paper: {
            sx: {
              height: '100%',
            },
          },
        }}
      >
        <DialogTitle>{t('Edit catchment area')}</DialogTitle>
        <DialogContent sx={{ minHeight: 400, p: 2 }}>
          <PlacePicker
            defaultValue={localitiesIds}
            onChange={v => setValue(v)}
            placeTypes={[Place_Type_Enum_Enum.Locality]}
          />
        </DialogContent>
        <DialogActions sx={{ pt: 0 }}>
          <Button onClick={() => setOpen(false)} variant="outlined">
            {t('Cancel')}
          </Button>
          <LoadingButton
            loading={loading}
            onClick={async () => {
              setLoading(true);
              setError(null);
              await setCatchmentArea({
                variables: {
                  userId,
                  objects: value.map(id => ({
                    place_id: id,
                    user_id: userId,
                  })),
                },
                refetchQueries: [GET_CATCHMENT_AREA],
              })
                .catch(e => setError(e))
                .then(() => setOpen(false))
                .finally(() => {
                  setLoading(false);
                  refetch();
                });
            }}
            variant="contained"
          >
            {t('Save')}
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </>
  );
};
